import { useContext, useEffect, useState } from 'react'
import { getBeatsApi, getVouchersApi, getVouchersDownloadApi, getUserListApi } from '../api/api'
import { showMessage } from '../context/MessageContext';
import { AuthContext } from '../context/AuthContext.jsx'
import { useOrganizationSettings } from '../context/OrganizationSettingsContext';
import { useCompany } from '../context/CompanyContext.jsx';
import Select from "react-select";
import Pagination from '../components/ui/Pagination.jsx'
import { usePagination } from '../hooks/usePagination.js'

export default function VouchersList() {
  const [limit] = useState(30); // Items per page
  const [total, setTotal] = useState(0);
  const {user} = useContext(AuthContext);
  const { settings } = useOrganizationSettings();
  const { companyId } = useCompany();
  const pagination = usePagination(total, limit);
  const [beats, setBeats] = useState([]);
  const [userList, setUserList] = useState([]);
  const [selectedBeat, setSelectedBeat] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserLabel, setSelectedUserLabel] = useState(null);

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)

  // Fetch all beats on mount (only if beatWise is enabled)
  useEffect(() => {
    if (settings.beatWise) {
      getBeatsApi()
        .then(res => setBeats(res.data?.data || res.data || []))
        .catch(() => setBeats([]));
    }
  }, [settings.beatWise]);
  
  // Fetch all users on mount (only if userWise is enabled)
  useEffect(() => {
    if (settings.userWise && user?.isOwner) {
      getUserListApi()
        .then(res => {
          const users = res.data?.data || res.data || [];
          setUserList(users);
          
          // Set default selection to logged-in user for owners
          if (user?.tallyUserId && users.length > 0) {
            const defaultUser = users.find(u => u.tallyUserId === user.tallyUserId);
            if (defaultUser) {
              setSelectedUserId(defaultUser.tallyUserId);
              setSelectedUserLabel(`${defaultUser.userName} - ${defaultUser.tallyUserId}`);
            }
          }
        })
        .catch(() => setUserList([]));
    }
  }, [settings.userWise, user?.isOwner, user?.tallyUserId]);



  useEffect(() => {
  setLoading(true);
  const params = {
    ...(settings.beatWise && selectedBeat ? { beatName: selectedBeat } : {}),
    // For userWise: owners can select, non-owners auto-filter by logged-in user
    ...(settings.userWise ? {
      tallyUserId: user?.isOwner ? (selectedUserId || user?.tallyUserId) : user?.tallyUserId
    } : {}),
    // Company filtering (Case 3 & 4) - use global company from context
    ...(settings.userWise && companyId ? { companyId: companyId } : {}),
    page: pagination.currentPage,
    limit,
  };
  getVouchersApi(params)
    .then((res) => {
      const data = res.data?.data || res.data || [];
      setRows(data.rows || data); // Adjust if your API returns { rows, total }
      setTotal(res.data.totalCount || 0);
    })
    .catch(() => {
      setRows([]);
      setTotal(0);
    })
    .finally(() => setLoading(false));
  }, [selectedBeat, selectedUserId, companyId, pagination.currentPage, limit, settings.beatWise, settings.userWise, user?.isOwner, user?.tallyUserId]);

  const handleBeatChange = e => {
    if (e === null) {
      setSelectedBeat(null);
    } else {
      setSelectedBeat(e.value);
    }
    pagination.resetPagination();
  };
  
  const handleUserChange = (selectedOption) => {
    // Only update if a selection was actually made (not when clicking outside)
    if (selectedOption === null) {
      // Only clear if explicitly cleared (X button), not on outside click
      setSelectedUserId(null);
      setSelectedUserLabel(null);
    } else if (selectedOption && selectedOption.value) {
      setSelectedUserId(selectedOption.value);
      setSelectedUserLabel(selectedOption.label);
    }
    pagination.resetPagination();    
  };

  const handleDownloadReport = async () => {
    if (!selectedBeat && !selectedUserId) {
      showMessage('Please select a Beat and User to download the report.', 'error');
      return;
    }
    try {
      setDownloading(true);
      const res = await getVouchersDownloadApi({ beatName: selectedBeat, tallyUserId: selectedUserId });
      const data = res.data;
      if (data.code === 200 && data.reportUrl) {
        setDownloading(false);
        window.open(data.reportUrl, '_blank');
      } else {
        setDownloading(false);
        showMessage(data.message || 'Failed to generate report.', 'error');
      }
    } catch (err) {
      console.error(err);
      setDownloading(false);
      showMessage('Error downloading report.', 'error');
    }
  };

  return (
    <div className="card">
      <h2>Outstanding Bill</h2>
      <div className="filter-container">
        <div className="d-flex flex-column flex-md-row gap-2" style={{ flex: 1 }}>
          {/* Beat */}
          {settings.beatWise && (
          <div className="filter-item">
            <label className="form-label small fw-500 mb-2 d-block">Beat</label>
            <Select
              options={beats.map(b => ({ value: b.beatName, label: b.beatName }))}
              value={selectedBeat ? { value: selectedBeat, label: selectedBeat } : null}
              onChange={handleBeatChange}
              placeholder="Select Beat"
              isClearable={true}
              isSearchable
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
          )}

          {/* User */}
          {settings.userWise && user?.isOwner &&(
          <div className="filter-item">
            <label className="form-label small fw-500 mb-2 d-block">User</label>
            <Select
              options={userList.map(u => ({ value: u.tallyUserId, label: `${u.userName} - ${u.tallyUserId}` }))}
              value={
                selectedUserId && selectedUserLabel
                  ? userList.find(u => u.tallyUserId === selectedUserId)
                    ? { value: selectedUserId, label: selectedUserLabel }
                    : null
                  : null
              }
              onChange={handleUserChange}
              onBlur={() => {}} // Prevent clearing on blur
              placeholder="Select User"
              isClearable={true}
              isSearchable
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
          )}
        </div>
        {/* <div className="filter-actions">
          { downloading ?
            <button className="btn btn-sm text-white bg-secondary mt-2 mt-md-0">
              Report Downloading...
            </button> 
            : 
            <button className="btn btn-sm btn-success mt-2 mt-md-0" onClick={handleDownloadReport}>
                Download Report
            </button>
          }
        </div> */}
      </div>
      {loading ? 'Loading...' : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>S.No</th>
                { settings.billWise && (
                  <>
                    <th>Bill Date</th>
                    <th>Bill Number</th>
                  </>
                )}
                <th>Customer</th>
                { settings.billWise && settings.beatWise && (
                  <>
                    <th>Beat</th>
                  </>
                )}
                { settings.billWise && (
                  <>
                    <th>Bill Amount</th>
                  </>
                )}
                <th>Paid Amount</th>
                <th>Balance</th>
                { settings.billWise && (
                  <>
                    <th>Bill Due Date</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '2rem', color: '#546e7a' }}>
                    No records available
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={r.id || idx}>
                    <td>{idx + 1}</td>
                    { settings.billWise && (
                      <>
                    <td>{(r.billDate || '').toString().slice(0, 10) ?? '-'}</td>
                    <td>{r.billNumber || '-'}</td>
                      </>
                    )}
                    <td>{r.customerName || '-'}</td>
                    { settings.billWise && settings.beatWise && (
                      <>
                        <td>{r.beatName || '-'}</td>
                      </>
                    )}
                    { settings.billWise && (
                      <>
                        <td>{r.billAmount ?? '-'}</td>
                      </>
                    )}
                    <td>{r.paidAmount ?? '-'}</td>
                    <td>{r.remainingAmount ?? '-'}</td>
                    { settings.billWise && (
                      <>
                        <td>{(r.billDueDate || '').toString().slice(0,10)}</td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {!loading && total > 0 && (
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={pagination.goToPage}
            />
          )}
        </div>
      )}
    </div>
  )
}
